import Constants from 'expo-constants';
import * as Updates from 'expo-updates';
import { Platform } from 'react-native';

import { BUILD_BUILT_AT, BUILD_STAMP } from '@/lib/build-info';

/**
 * Tiny wrapper around expo-updates so the Settings screen can offer a
 * single "Check for updates" button that behaves sensibly across platforms.
 *
 * Default behavior of the runtime is already check-on-launch (configured
 * by `eas update:configure` → `app.json` `updates.url` + runtimeVersion).
 * The manual button is for parents / impatient testers who don't want to
 * force-close + reopen.
 */

export type UpdateCheckResult =
  /** Web doesn't use expo-updates — a hard refresh of the browser gets
   *  the latest static bundle from the CDN. */
  | { kind: 'web' }
  /** expo-updates isn't enabled in this build (e.g. dev client without
   *  the runtime). User shouldn't normally see this. */
  | { kind: 'no-runtime' }
  /** No newer bundle published for this runtime version. */
  | { kind: 'up-to-date' }
  /** Found, downloaded, and about to reload. App will restart. */
  | { kind: 'updated' }
  /** Network / server / native error. */
  | { kind: 'failed'; message: string };

/**
 * Silent on-launch update flow. Designed for the root `_layout.tsx`:
 * checks for a newer JS bundle, downloads it in the background, and
 * reloads to apply — no force-close required. Skipped on web (no
 * expo-updates runtime), on dev clients (would interfere with Metro), and
 * if the user has been in the app long enough that a hot-reload would
 * interrupt them (we cap the reload window at 8 seconds after boot).
 *
 * All errors are swallowed — the manual "Check for updates" button in
 * Settings is the audible-failure path; this one is supposed to be
 * invisible when it works.
 */
export async function autoApplyUpdateOnLaunch(): Promise<void> {
  if (Platform.OS === 'web') return;
  if (!Updates.isEnabled) return;
  try {
    const result = await Updates.checkForUpdateAsync();
    if (!result.isAvailable) return;
    await Updates.fetchUpdateAsync();
    // Wave 65 (long-standing user report: "it loads in and then
    // reloads"): the old 60-second window reloaded AFTER the first
    // page had rendered — every cold start following an OTA (and
    // always the first launch of a fresh install) visibly flashed.
    // Now we reload ONLY while the boot preloader is still covering
    // the screen, where a reload is invisible. Any later and the
    // downloaded bundle simply becomes the one the NEXT launch boots —
    // expo-updates launches the newest fetched update automatically,
    // so nothing is lost, and no session ever gets yanked.
    const { isBootReady } = await import('@/lib/boot-ready');
    if (!isBootReady()) {
      await Updates.reloadAsync();
    }
  } catch (err) {
    // Network down, fetch errored, reload threw — best-effort path.
    console.warn('autoApplyUpdateOnLaunch silent failure', err);
  }
}

export async function checkAndApplyUpdate(): Promise<UpdateCheckResult> {
  if (Platform.OS === 'web') return { kind: 'web' };
  if (!Updates.isEnabled) return { kind: 'no-runtime' };
  try {
    const result = await Updates.checkForUpdateAsync();
    if (!result.isAvailable) return { kind: 'up-to-date' };
    await Updates.fetchUpdateAsync();
    // Hand off — reloadAsync replaces the JS runtime with the new bundle.
    // The promise typically does not resolve (process restarts), so we
    // return the "updated" status BEFORE awaiting to be safe.
    void Updates.reloadAsync();
    return { kind: 'updated' };
  } catch (err) {
    return { kind: 'failed', message: err instanceof Error ? err.message : String(err) };
  }
}

export interface VersionInfo {
  /** appVersion-policy means this matches the APK's marketing version. */
  runtimeVersion: string;
  /** EAS Update channel (preview / production / dev). */
  channel: string;
  /** Short UUID of the active JS bundle, for support troubleshooting. */
  bundleId: string;
  /** When the active bundle was published. Null if running the embedded
   *  bundle that shipped with the binary. */
  bundleDate: Date | null;
}

export function getVersionInfo(): VersionInfo {
  // Every field is wrapped because expo-updates' static getters can throw
  // in unusual states (e.g. updates disabled at native level, missing
  // manifest) and any throw here brings down whatever page is rendering.
  // We had a Settings-page crash traced to this — never again.
  const safe = <T>(read: () => T, fallback: T): T => {
    try { return read(); } catch { return fallback; }
  };
  const rt = safe(() => Updates.runtimeVersion, '');
  const ch = safe(() => Updates.channel, '');
  const updateId = safe(() => Updates.updateId, null as string | null);
  const created = safe(() => Updates.createdAt, null as Date | null);

  // Web fallback: expo-updates has no manifest on web, so every field
  // above is empty. Pull the marketing version from app.json's `version`
  // (via Constants) and stamp the page-load time as a "session" date so
  // the Settings row at least shows *something* identifiable to the user.
  const appVersion =
    (Constants.expoConfig?.version as string | undefined) ?? '1.0.0';
  const isWeb = Platform.OS === 'web';

  // On web, prefer the build-time stamp (generated by stamp-build.mjs
  // before each `expo export`). Falls back to 'static' if the stamp file
  // still says 'dev' (local dev server didn't run the stamp script).
  // The `as string` widen — TypeScript otherwise narrows BUILD_STAMP to
  // its literal-from-the-source value and refuses the !== comparison.
  const stamp = BUILD_STAMP as string;
  const webBundleId = stamp && stamp !== 'dev' ? stamp : 'static';
  const webBuiltAt = BUILD_BUILT_AT ? new Date(BUILD_BUILT_AT) : null;

  return {
    runtimeVersion: typeof rt === 'string' && rt.length > 0 ? rt : appVersion,
    channel: typeof ch === 'string' && ch.length > 0 ? ch : (isWeb ? 'web' : 'dev'),
    bundleId: typeof updateId === 'string' && updateId.length > 0
      ? updateId.slice(0, 8)
      : (isWeb ? webBundleId : 'embedded'),
    // Defensive: Date instance check + valid-time check. Some bridges
    // hand back a string here instead of a Date, and toLocaleDateString
    // on a string blows up.
    bundleDate: created instanceof Date && !isNaN(created.getTime())
      ? created
      : (isWeb && webBuiltAt && !isNaN(webBuiltAt.getTime())
        ? webBuiltAt
        : null),
  };
}
